import Button from '../ui/Button'
import { headingReveal, stepProgress } from '../../animations/gsapAnimations'
import { useGsapContext } from '../../hooks/useGsapReveal'
import { useSection } from '../../cms/cms-context'

/* The four steps from showroom to driveway.
 *
 * Vertical rather than the brief's horizontal band: six steps of real prose
 * do not fit across a phone, and /financing carries the horizontal treatment
 * so the site shows both.
 *
 * The four advantage cards that used to close this section are their own
 * section now — see Advantages — so the console can move them, or drop them,
 * without taking the steps with them. The spacing is unchanged: the 96px this
 * section leaves underneath is the margin the cards used to carry above.
 */
const STEPS = [
  {
    id: 'pick',
    content: {
      title: 'Pick the car',
      body: 'Choose from live floor stock or tell us the variant and colour you want — we source it from the dealership network, usually inside a week.',
    },
  },
  {
    id: 'banks',
    content: {
      title: 'We shop the banks',
      body: 'One set of documents goes to every partner bank at once. You get the markup, tenure and instalment from each, side by side, in writing.',
    },
  },
  {
    id: 'approval',
    content: {
      title: 'Approval in 48 hours',
      body: 'Our team runs the file end to end — verification, valuation, tracker and insurance — so you sign once instead of five times.',
    },
  },
  {
    id: 'keys',
    content: {
      title: 'Keys at the lounge',
      body: 'Delivery, registration and number plate handled. You collect from the lounge, or we drop it to your door anywhere in Islamabad or Rawalpindi.',
    },
  },
]

function Process() {
  const cms = useSection()
  const steps = cms.items(STEPS)

  const track = useGsapContext<HTMLOListElement>((scope) => stepProgress(scope, 'y'))

  /* The headline opens the way every other section's does. */
  const head = useGsapContext<HTMLDivElement>((scope) => headingReveal(scope))

  const accent = cms.text('titleAccent', 'showroom to driveway')

  return (
    <section id="process" aria-labelledby="process-title" className="relative pt-24 pb-24 sm:pt-32">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-20">
          {/* ---------- headline ---------- */}
          <div ref={head} className="lg:sticky lg:top-28 lg:self-start">
            <p
              data-heading="eyebrow"
              {...cms.field('eyebrow')}
              className="flex items-center gap-3 label text-lime"
            >
              <span aria-hidden="true" data-heading="rule" className="h-px w-9 coachline" />
              {cms.text('eyebrow', 'How it works')}
            </p>
            <h2
              id="process-title"
              data-heading="title"
              {...cms.field('title')}
              className="mt-5 font-display text-display-2 text-platinum"
            >
              {cms.text('title', 'Four steps from')}
              {accent ? (
                <>
                  {' '}
                  <span className="text-lime-grad">{accent}</span>
                </>
              ) : null}
            </h2>
            <p
              data-heading="body"
              {...cms.field('lede')}
              className="mt-5 max-w-[44ch] text-[15.5px] leading-relaxed text-steel"
            >
              {cms.text(
                'lede',
                'Leasing a car in Pakistan usually means six visits and a folder of photocopies. We compressed it into one conversation.',
              )}
            </p>

            <Button
              variant="ghost"
              size="lg"
              to={cms.text('ctaHref', '/contact')}
              arrow
              data-heading="body"
              {...cms.field('cta')}
              className="mt-9"
            >
              {cms.text('cta', 'Book a lounge visit')}
            </Button>
          </div>

          {/* ---------- steps ---------- */}
          <ol
            ref={track}
            className="relative flex flex-col gap-px overflow-hidden rounded-2xl border border-line bg-line shadow-card"
          >
            {/* The rail the scroll draws down as the steps light up. */}
            <span
              aria-hidden="true"
              data-progress-line
              className="absolute top-0 bottom-0 left-0 z-1 w-0.5 bg-lime"
            />
            {steps.map((step, index) => (
              <li
                key={step.id}
                data-progress-step
                {...step.props}
                className="group relative flex gap-6 bg-raised px-6 py-8 transition-colors duration-300 hover:bg-surface sm:px-9 sm:py-10"
              >
                {/* The step number at display size — the way a stage number
                    is painted on the workshop floor. */}
                <span
                  aria-hidden="true"
                  data-progress-num
                  className="shrink-0 font-display text-[30px] leading-none font-bold text-lime/25 tabular-nums"
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0">
                  <h3 className="font-display text-[19px] font-semibold tracking-[-0.02em] sm:text-xl">
                    {step.content.title}
                  </h3>
                  <p className="mt-2.5 max-w-[58ch] text-[14.5px] leading-relaxed text-steel">
                    {step.content.body}
                  </p>
                </div>
                {/* Accent rail that lights up on hover. */}
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 left-0 w-px scale-y-0 bg-linear-to-b from-transparent via-lime to-transparent transition-transform duration-400 ease-lux group-hover:scale-y-100"
                />
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  )
}

export default Process
